import { useEffect, useRef, useState, type FormEvent } from "react";

import { Alert, AlertDescription, AlertTitle, Button, Input } from "@sirdab/ui";

import { useApi, type ApiFailure } from "../../api";
import { ErrorState, Field, StepActions } from "../../components";
import { useFlow } from "../../flow";
import { useMessages, usePortalCopy } from "../../i18n";
import {
  clearRememberedShopper,
  readRememberedShopper,
  writeRememberedShopper,
} from "../../session";
import { useTheme } from "../../theme";
import { takePrefill } from "../prefill";
import { OfflineNote, useFailureText } from "../shared";
import { StepScaffold } from "../StepScaffold";

/**
 * Step 1 of docs/08, the order number path: the order reference and nothing else.
 *
 * It is a shortcut AFTER sign-in, never a way in. `POST /api/v1/returns/shopper/
 * merchants/:merchantSlug/lookup` requires the verified session (`require_session!`) and
 * takes the phone from it, so this form sends the bearer from `useFlow()` and no phone at
 * all. A phone field here would be dead input that also lied: typing a different number
 * would still have looked the order up under the verified one. `../LookupScreen.tsx`
 * therefore only ever opens this form once a token exists.
 *
 * One failure surface for whatever comes back, with the same fixed hint under it in every
 * case: a wrong reference and a server that is simply unhappy read identically, so the
 * portal cannot be turned into an order-enumeration oracle by reading its error copy.
 *
 * What she typed is remembered for the tab (`../session`), so an interrupted return
 * resumes in one tap. Loading sits on the button, not the page (docs/08).
 *
 * A Salla storefront may hand a pick over in the URL fragment (`../prefill.ts`): the order
 * reference fills the field and, once signed in, the lookup runs on its own through the
 * very same path as the button, and a refusal leaves the field filled to correct.
 */
export function ManualLookup({
  /** The deep-link screen sets this when a minted link failed to resolve. */
  linkFailed = false,
  /** Back to the phone and code path, when this form was reached from it. */
  onUsePhone,
}: {
  linkFailed?: boolean;
  onUsePhone?: (() => void) | undefined;
}): React.JSX.Element {
  const api = useApi();
  const { t } = useMessages();
  const c = usePortalCopy();
  const { applyMerchantTheme } = useTheme();
  const { merchantSlug, orderRef, phone, shopperToken, update, next } =
    useFlow();
  const failureText = useFailureText();

  const prefill = useRef(takePrefill());
  const [remembered, setRemembered] = useState(() =>
    prefill.current.orderRef !== null
      ? null
      : readRememberedShopper(merchantSlug),
  );
  const [refValue, setRefValue] = useState(
    () => prefill.current.orderRef || orderRef || remembered?.orderRef || "",
  );
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  const refError =
    touched && refValue.trim() === ""
      ? t("validation.order_ref_required")
      : undefined;

  async function run(rawRef: string): Promise<void> {
    const trimmedRef = rawRef.trim();

    setFailure(null);
    setBusy(true);
    const result = await api.lookup(
      { merchantSlug, orderRef: trimmedRef },
      shopperToken ?? undefined,
    );
    setBusy(false);

    if (!result.ok) {
      setFailure(result);
      return;
    }

    // Only a lookup the server accepted is worth resuming from. The phone is the verified
    // one already in the flow, which is also the one the server filed the lookup under.
    writeRememberedShopper({
      merchantSlug,
      orderRef: trimmedRef,
      phone,
    });

    applyMerchantTheme(result.data.policy.theme);
    update({
      orderRef: trimmedRef,
      lookup: result.data,
      lines: [],
    });
    next();
  }

  // The storefront hand-off: the fragment named an order, so look it up as the shopper
  // would have. Once per mount; the fragment was cleared when it was read.
  const autoRan = useRef(false);
  useEffect(() => {
    const { orderRef: ref } = prefill.current;
    if (autoRan.current || ref === null) return;
    autoRan.current = true;
    setTouched(true);
    void run(ref);
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setTouched(true);
    if (refValue.trim() === "") return;
    await run(refValue);
  }

  function forgetAndStartOver(): void {
    clearRememberedShopper();
    setRemembered(null);
    setRefValue("");
    setTouched(false);
    setFailure(null);
  }

  return (
    <StepScaffold
      step="lookup"
      titleKey="screen.lookup.title"
      descriptionKey="screen.lookup.description"
    >
      <form
        noValidate
        onSubmit={(event) => void submit(event)}
        className="flex flex-col gap-4"
      >
        {linkFailed ? (
          <Alert variant="warning">{c("link.failed")}</Alert>
        ) : null}

        {remembered !== null ? (
          <Alert>
            <AlertTitle>{c("lookup.resume_title")}</AlertTitle>
            <AlertDescription>
              <p>{c("lookup.resume_body")}</p>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <Button
                  type="button"
                  size="xl"
                  loading={busy}
                  onClick={() => void run(remembered.orderRef)}
                  className="sm:flex-1"
                >
                  {c("action.resume_lookup")}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="xl"
                  onClick={forgetAndStartOver}
                  className="sm:flex-1"
                >
                  {c("action.use_other_order")}
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        ) : null}

        <OfflineNote />

        <Field
          label={t("field.order_ref.label")}
          hint={t("field.order_ref.hint")}
          error={refError}
          required
        >
          {(props) => (
            <Input
              {...props}
              size="touch"
              // Latin-shaped data: the reference reads the same way on the shopper's
              // order confirmation in either language.
              dir="ltr"
              type="text"
              inputMode="text"
              autoComplete="off"
              autoCapitalize="characters"
              value={refValue}
              placeholder={t("field.order_ref.placeholder")}
              onChange={(event) => setRefValue(event.target.value)}
            />
          )}
        </Field>

        {failure !== null ? (
          // One surface for every failure. A wrong reference, an order that belongs to
          // someone else and a session the server no longer accepts all land here with the
          // server's own words, followed by the same fixed hint in every case.
          <ErrorState message={failureText(failure)}>
            <p>{c("lookup.not_found_hint")}</p>
          </ErrorState>
        ) : null}

        <StepActions
          nextType="submit"
          nextLabel={t("action.find_order")}
          nextLoading={busy}
        />

        {onUsePhone !== undefined ? (
          <Button
            type="button"
            variant="link"
            size="xl"
            onClick={onUsePhone}
            className="self-center"
          >
            {t("action.use_phone_instead")}
          </Button>
        ) : null}
      </form>
    </StepScaffold>
  );
}
